import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { apiClient } from '../../lib/api-client';
import { cacheUtils, queryKeys } from '../../lib/query-client';
import { authApi } from '../../services/api/auth-api';

// Hook for requesting OTP
export function useRequestOtp() {
  return useMutation({
    mutationFn: authApi.requestOtp,
    onError: (error) => {
      console.error('Failed to request OTP:', error);
    },
  });
}

// Hook for verifying OTP and logging in
export function useVerifyOtp() {
  return useMutation({
    mutationFn: authApi.verifyOtp,
    onSuccess: (data) => {
      // Store the access token
      if (data.accessToken) {
        apiClient.setAuthToken(data.accessToken);
      }

      // Store user info in localStorage
      try {
        const userInfo = {
          email: data.email,
          name: data.name,
          avatar: data.avatar,
        };
        localStorage.setItem('admin_user_info', JSON.stringify(userInfo));
      } catch (error) {
        console.warn('Failed to store user info:', error);
      }

      // Invalidate and refetch any auth-related queries
      cacheUtils.invalidateFeature('auth');

      console.log('Login successful');
    },
    onError: (error) => {
      console.error('Failed to verify OTP:', error);
    },
  });
}

// Hook for logging out
export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      // Clear the auth token
      apiClient.clearAuthToken();

      // Clear user info
      try {
        localStorage.removeItem('admin_user_info');
      } catch (error) {
        console.warn('Failed to clear user info:', error);
      }

      // Clear all cached data
      cacheUtils.clearAll();

      return true;
    },
    onSuccess: () => {
      console.log('Logout successful');
    },
  });
}

// Hook for getting current user info
export function useUser() {
  const [user, setUser] = useState<{
    email: string;
    name: string;
    avatar: string | null;
  } | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('admin_user_info');
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load user info:', error);
    }
  }, []);

  return user;
}

// Hook for checking if user is authenticated
export function useIsAuthenticated() {
  return useQuery({
    queryKey: queryKeys.auth.profile(),
    queryFn: async () => {
      const hasToken = apiClient.hasAuthToken();
      if (!hasToken) return false;

      // You can add additional token validation logic here
      return true;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry auth checks
  });
}

// Hook for getting current auth state
export function useAuthState() {
  const { data: isAuthenticated, isLoading } = useIsAuthenticated();

  return {
    isAuthenticated: !!isAuthenticated,
    isLoading,
    hasToken: apiClient.hasAuthToken(),
  };
}
